import React, { useContext, useMemo } from 'react';
import { Grid, Stack, Typography, Select, ListSubheader, MenuItem, Button, Paper, IconButton, ListItemButton, ListItemAvatar, Avatar, ListItemText, Chip, Tooltip } from '@mui/material';
import { ArrowBackRounded, FileDownloadRounded, WorkspacePremiumRounded, PeopleOutlined, SwapHorizRounded, TimelineRounded, OpenInNew } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router';
import { useGetProcedureItemUsageTrendsQuery, useGetProcedureItemsQuery, useGetProcedureQuery, useGetProcedureCardsQuery } from '../../api/procedureSlice';
import { ResponsiveLine } from '@nivo/line';
import { useGetItemQuery } from '../../api/itemDetailSlice';
import { NumericFormat } from 'react-number-format';
import { UserContext } from '../Hooks/useUser';
import { theme } from '../../theme';


function ProcedureItemAnalysis() {
    const { procedureId, itemId } = useParams();
    const { user } = useContext(UserContext);
    const navigate = useNavigate();
    const [isGoldStandard, setIsGoldStandard] = React.useState(false);
    const [showAllAlternatives, setShowAllAlternatives] = React.useState(false);
    const [showAllPhysicians, setShowAllPhysicians] = React.useState(false);

    const { data: itemUsageTrendsData = [], isLoading, isSuccess } = useGetProcedureItemUsageTrendsQuery({ procedureId, itemId });
    const { data: procedureItems = [] } = useGetProcedureItemsQuery(procedureId);
    const { data: item } = useGetItemQuery(itemId);
    const { data: cards = [] } = useGetProcedureCardsQuery(procedureId);
    const { data: procedure } = useGetProcedureQuery(procedureId);
    
    function exportItemData(itemId) {
        return;
    }

    function approveAsGoldStandard() {
        setIsGoldStandard(true);
    }

    const itemsGroupedByCategory = useMemo(() => {
        const categoryMap = {};
        procedureItems.forEach((item) => {
            if (!categoryMap[item.category]) {
                categoryMap[item.category] = [];
            }
            categoryMap[item.category].push(item);
        });
        return categoryMap;
    }, [procedureItems]);

    const categories = useMemo(() => {
        const uniqueCategories = new Set(procedureItems.map((item) => item.category));
        return Array.from(uniqueCategories);
    }, [procedureItems]);

    const last12Months = useMemo(() => {
        const now = new Date();
        const months = [];
        for (let i = 11; i >= 0; i--) {
            const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            months.push(`${year}-${month}`);
        }
        return months;
    }, []);

    const chartData = useMemo(() => {
        const usedByMonth = {};
        const casesByMonth = {};
        itemUsageTrendsData.forEach((trend) => {
            usedByMonth[trend.month_label] = trend.total_used_quantity;
            casesByMonth[trend.month_label] = trend.num_cases;
        });
        return [{
            id: 'Num Used',
            data: last12Months.map((month) => ({
                x: month,
                y: usedByMonth[month] ?? 0
            }))
        }, {
            id: 'Num Cases',
            data: last12Months.map((month) => ({
                x: month,
                y: casesByMonth[month] ?? 0
            }))
        }];
    }, [itemUsageTrendsData, last12Months]);

    const sortedAlternatives = useMemo(() => {
        if (!item) return [];
        return (itemsGroupedByCategory[item.category] ?? [])
            .filter((procedureItem) => procedureItem.id !== item.id)
            .sort((a, b) => a.cost - b.cost);
    }, [itemsGroupedByCategory, item]);

    return (
        <Grid container spacing={2} pb={4}>
            <Grid item xs={12} sx={{ position: 'sticky', top: 64, zIndex: 5, backgroundColor: theme.palette.background.white, borderBottom: '1px solid', borderColor: 'divider' }} position='sticky'>
                <Stack direction='row' px={2} pb={2} justifyContent='space-between'>
                    <Stack direction='row' spacing={2} alignItems='center' flex={1} minWidth={0} mr={2}>
                        <IconButton onClick={() => navigate(`/procedures/${procedureId}/analysis`)}>
                            <ArrowBackRounded fontSize='medium' />
                        </IconButton>
                        <Stack spacing={0.5} minWidth={0}>
                            <Tooltip title={item?.name} placement='top' arrow>                                                
                                <Typography variant='bold' fontSize='20px' sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                    {item?.name}
                                </Typography>
                            </Tooltip>
                            <Typography variant='bold' fontSize='16px'>SKU: {item?.part_number} - {item?.vendor?.name}</Typography>
                        </Stack>
                    </Stack>
                    <Stack>
                        <Typography variant='normal' fontSize='14px'>Quick Switch</Typography>
                        <Select
                            size='small'
                            sx={{ minWidth: '200px' }}
                            value={itemId}
                            onChange={(e) => { navigate(`/procedures/${procedureId}/items/${e.target.value}`); }}
                        >
                            {categories.flatMap((category) => {
                                const categoryItems = procedureItems.filter((item) => item.category === category);
                                if (categoryItems.length === 0) { return []; }
                                return [
                                    <ListSubheader key={category}>{category}</ListSubheader>,
                                    ...categoryItems.map((procedureItem) => (
                                        <MenuItem key={procedureItem.id} value={procedureItem.id}>{procedureItem.name}</MenuItem>
                                    ))
                                ];
                            })}
                        </Select>
                    </Stack>
                    { // Functionality for these buttons is still forthcoming so commenting out to prevent users from accessing. 
                    /* <Stack direction='row' spacing={2} alignItems='center'>
                        <Button variant='outlined' startIcon={<FileDownloadRounded />} onClick={() => exportItemData(item.id)}>
                            <Typography variant='normal' fontSize='14px'>Export Data</Typography>
                        </Button>
                        { isGoldStandard ?
                            <Button variant='contained' startIcon={<WorkspacePremiumRounded />} style={{ backgroundColor: '#ffd700', color: '#000' }} disabled>
                                <Typography variant='normal' fontSize='14px'>Gold Standard</Typography>
                            </Button>
                            :     
                            <Button variant='contained' startIcon={<WorkspacePremiumRounded />} onClick={() => approveAsGoldStandard()}>
                                <Typography variant='normal' fontSize='14px'>Approve as Gold Standard</Typography>
                            </Button>
                        }
                    </Stack> */}
                </Stack>
            </Grid>
            <Grid container mx={3} spacing={3} mt={1}>
                <Grid item xs={8} spacing={3}>
                    <Paper elevation={2} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
                        <Stack direction='row' alignItems='center' justifyContent='space-between'>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <TimelineRounded />
                                <Typography variant='h6' fontWeight={700}>Usage Analysis</Typography>
                            </Stack>
                            <Button variant='text' startIcon={<OpenInNew />} onClick={() => navigate(`/organizations/${user.hospital.organization_id}/items/${itemId}/itemAnalytics`)}>
                                View All Item Usage
                            </Button>
                        </Stack>
                        <Typography variant='light' fontSize='14px'>Trailing 12-month usage in {procedure?.name} procedures.</Typography>
                        <div style={{ height: 400 }}>
                            <ResponsiveLine
                                data={chartData}
                                margin={{ top: 50, right: 110, bottom: 50, left: 60 }}
                                curve='monotoneX'
                                yScale={{
                                    type: 'linear',
                                    min: 0,
                                    max: 'auto',
                                    stacked: false,
                                    reverse: false
                                }}
                                axisTop={null}
                                axisRight={null}
                                axisBottom={{
                                    orient: 'bottom',
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: -35,
                                    legend: 'Month',
                                    legendOffset: 46,
                                    legendPosition: 'middle',
                                    tickValues: last12Months
                                }}
                                axisLeft={{
                                    orient: 'left',
                                    tickSize: 5,
                                    tickPadding: 5,
                                    tickRotation: 0,
                                    legend: 'Usage Count',
                                    legendOffset: -40,
                                    legendPosition: 'middle',
                                }}
                                pointSize={10}
                                pointBorderWidth={2}
                                pointBorderColor={{ from: 'serieColor' }}
                                pointLabel="y"
                                pointLabelYOffset={-12}
                                useMesh={true}
                                legends={[
                                    {
                                        anchor: 'bottom-right',
                                        direction: 'column',
                                        translateX: 100,
                                        itemWidth: 80,
                                        itemHeight: 22,
                                        symbolShape: 'circle'
                                    }
                                ]}
                            />
                        </div>
                    </Paper>
                    <Paper elevation={2} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
                        <Typography variant='h6' fontWeight={700}>Item Information</Typography>
                        <Typography variant='light' fontSize='14px'>Additional details about this item.</Typography>
                        <Stack direction='row' spacing={2} mt={2} flexWrap='wrap' useFlexGap>
                            <Stack spacing={0.5}>
                                <Typography variant='normal' fontSize='14px'>Part Number</Typography>
                                <Typography variant='bold' fontSize='16px'>{item?.part_number}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant='normal' fontSize='14px'>Vendor</Typography>
                                <Typography variant='bold' fontSize='16px'>{item?.vendor?.name}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant='normal' fontSize='14px'>Category</Typography>
                                <Typography variant='bold' fontSize='16px'>{item?.category}</Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant='normal' fontSize='14px'>Unit Cost</Typography>
                                <Typography variant='bold' fontSize='16px'><NumericFormat value={item?.cost / 100} displayType={'text'} thousandSeparator={true} prefix={'$'} decimalScale={2} /></Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <Typography variant='normal' fontSize='14px'>External ID</Typography>
                                <Typography variant='bold' fontSize='16px'>{item?.external_id}</Typography>
                            </Stack>
                        </Stack>
                    </Paper>
                </Grid>
                <Grid item xs={4} spacing={3}>
                    <Paper elevation={2} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
                        <Stack direction='row' alignItems='center' justifyContent='space-between'>
                            <Stack direction='row' spacing={1} alignItems='center'>
                                <PeopleOutlined />
                                <Typography variant='h6' fontWeight={700}> Physicians</Typography>
                            </Stack>
                            <Chip size='small' label={`${cards.length}`} />
                        </Stack>
                        { isSuccess && cards.length === 0 &&
                            <Typography variant='light' fontSize='14px' mt={1}>No physicians have used this item in this procedure in the past year.</Typography>
                        }
                        { isSuccess && cards.length > 0 &&
                            <Stack mt={1}>
                                { (showAllPhysicians ? cards : cards.slice(0, 5)).map((card) => (
                                    <ListItemButton key={card.id}>
                                        <ListItemAvatar>
                                            <Avatar>
                                                {card.physician_name.split(' ').map((namePart) => namePart[0]).join('')}
                                            </Avatar>
                                        </ListItemAvatar>
                                        <ListItemText primary={`Dr. ${card.physician_name}`} secondary={card.specialty} />
                                    </ListItemButton>
                                ))}
                                { cards.length > 5 &&
                                    <Button variant='text' size='small' onClick={() => setShowAllPhysicians((prev) => !prev)}>
                                        {showAllPhysicians ? 'Show less' : `Show ${cards.length - 5} more`}
                                    </Button>
                                }
                            </Stack>
                        }
                    </Paper>
                    <Paper elevation={2} sx={{ p: 3, mt: 2, borderRadius: 2 }}>
                        <Stack direction='row' spacing={1} alignItems='center'>
                            <SwapHorizRounded />
                            <Typography variant='h6' fontWeight={700}>Alternatives</Typography>
                        </Stack>
                        { isSuccess && sortedAlternatives.length === 0 &&
                            <Typography variant='light' fontSize='14px' mt={1}>No alternatives found in this procedure.</Typography>
                        }
                        { isSuccess && sortedAlternatives.length > 0 &&
                            <Stack spacing={2} mt={1}>
                                { (showAllAlternatives ? sortedAlternatives : sortedAlternatives.slice(0, 5)).map((alternativeItem) => (
                                    <Stack key={alternativeItem.id} p={2} spacing={1} sx={{ backgroundColor: '#f8fafc', borderRadius: 1, border: '2px solid', borderColor: '#cbd5e1', cursor: 'pointer' }} width='100%' onClick={() => navigate(`/procedures/${procedureId}/items/${alternativeItem.id}`)}>
                                        <Typography variant='bold' fontSize='16px'>{alternativeItem.name}</Typography>
                                        <Typography variant='light' fontSize='14px'>SKU: {alternativeItem.part_number} - {alternativeItem.vendor.name}</Typography>
                                        <Typography variant='normal' fontSize='14px' color={alternativeItem.cost - item.cost < 0 ? 'green' : 'red'}>
                                            <NumericFormat 
                                                value={Math.abs(item.cost - alternativeItem.cost) / 100} 
                                                displayType={'text'} 
                                                thousandSeparator={true}
                                                prefix={item.cost - alternativeItem.cost >= 0 ? '-$' : '+$'}
                                                suffix={' /unit'} 
                                                decimalScale={0}
                                            />
                                        </Typography>
                                    </Stack>
                                ))}
                                { sortedAlternatives.length > 5 &&
                                    <Button variant='text' size='small' onClick={() => setShowAllAlternatives((prev) => !prev)}>
                                        {showAllAlternatives ? 'Show less' : `Show ${sortedAlternatives.length - 5} more`}
                                    </Button>
                                }
                            </Stack>
                        }
                    </Paper>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default ProcedureItemAnalysis;
